import { Menu, Transition } from "@headlessui/react";
import {
  ArrowDownTrayIcon,
  ChevronDownIcon,
  DocumentDuplicateIcon,
  PencilSquareIcon,
  TrashIcon,
} from "@heroicons/react/20/solid";
import { ArrowPathIcon, BookmarkIcon } from "@heroicons/react/24/outline";
import { Fragment, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DeleteChartConfirmDialog } from "../../../../components/dialogs/delete-chart-confirm-dialog";
import { useAuth } from "../../../../contexts/AuthContext";
import { ChartListResponse, ChartStatusColorEnum, ChartStatusEnum } from "../../../../models/chart";
import { getDate } from "../../../../utils/formatDate";

type AdminChartDisplayProps = {
  chart: ChartListResponse;
  getDate: (date: string) => string;
  deleteChart: (id: string) => void;
  downloadChart: (chartId: string, type: "download" | "print") => Promise<void>;
  duplicateChart: (id: string) => void;
  regenerateChart: (id: string) => void;
  backgroundColor: string;
  markChartsAsPrinted: (chartIds: string[]) => void;
};

const ClerkChartDisplay = (props: AdminChartDisplayProps) => {
  const [isDeleteConfirmationOpen, setDeleteConfirmationOpen] =
    useState<boolean>(false);

  const {
    chart,
    deleteChart,
    downloadChart,
    backgroundColor,
    regenerateChart,
    markChartsAsPrinted
  } = props;
  const navigate = useNavigate();
  const auth = useAuth();

  return (
    <>
      <div
        className={`flex items-center w-full justify-items-center py-2 ${backgroundColor}`}
      >
        <div className="grid w-full grid-cols-7 p-3 ml-1 text-sm font-poppins justify-items-start">
          <p className="font-semibold">{chart.precinct}</p>
          <p className="font-semibold text-primary-blue">
            {chart.municipality}
          </p>
          <p>{chart.county}</p>
          <p>{getDate(chart.electionDate)}</p>
          <p>{getDate(chart.modifiedAt)}</p>
          <div className="flex items-start gap-2">
            <p
              className={`${ChartStatusColorEnum[
                chart.status as keyof typeof ChartStatusColorEnum
              ]
                } px-1 rounded-sm uppercase text-xs`}
            >
              {chart.status === "InProgress" ? "In Progress" : chart.status}
            </p>
          </div>
          <Menu as="div" className="relative inline-block text-left">
            <div>
              <Menu.Button className="inline-flex justify-center w-full p-1 text-sm border rounded-md outline-none border-primary-blue text-primary-blue">
                Actions
                <ChevronDownIcon
                  className="w-5 h-5 ml-2 -mr-1 outline-none text-primary-blue hover:text-primary-blue"
                  aria-hidden="true"
                />
              </Menu.Button>
            </div>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Menu.Items className="absolute right-0 z-10 w-48 p-2 mt-4 origin-top-right bg-white divide-y divide-gray-100 rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none hover:cursor-pointer">
                <div className="text-xs">
                  {chart.downloadLink && (
                    <Menu.Item>
                      {({ active }) => (
                        <div className="flex justify-center text-center">
                          <ArrowDownTrayIcon
                            className={`${active
                              ? "bg-primary-blue text-white border-1 border-primary-blue"
                              : "text-primary-blue"
                              } h-10 w-10 p-2 rounded-l-md`}
                            aria-hidden="true"
                          />
                          <span
                            onClick={() => downloadChart(chart.id, "download")}
                            className={`${active
                              ? "bg-primary-blue text-white"
                              : "text-primary-blue"
                              } group flex rounded-r-md items-center w-full text-md`}
                          >
                            Download Chart
                          </span>
                        </div>
                      )}
                    </Menu.Item>
                  )}
                  {auth.isAdmin && chart.downloadLink && (
                    <Menu.Item>
                      {({ active }) => (
                        <div className="flex justify-center text-center">
                          <ArrowDownTrayIcon
                            className={`${active
                              ? "bg-primary-blue text-white border-1 border-primary-blue"
                              : "text-primary-blue"
                              } h-10 w-10 p-2 rounded-l-md`}
                            aria-hidden="true"
                          />
                          <span
                            onClick={() => downloadChart(chart.id, "print")}
                            className={`${active
                              ? "bg-primary-blue text-white"
                              : "text-primary-blue"
                              } group flex rounded-r-md items-center w-full text-md`}
                          >
                            Download Print Chart
                          </span>
                        </div>
                      )}
                    </Menu.Item>
                  )}
                  {auth.isAdmin && chart.status === ChartStatusEnum.Completed && (
                    <Menu.Item>
                      {({ active }) => (
                        <div className="flex justify-center text-center">
                          <BookmarkIcon
                            className={`${active
                              ? "bg-primary-blue text-white border-1 border-primary-blue"
                              : "text-primary-blue"
                              } h-10 w-10 p-2 rounded-l-md`}
                            aria-hidden='true'
                          />
                          <span
                            className={`${active
                              ? "bg-primary-blue text-white"
                              : "text-primary-blue"
                              } group flex rounded-r-md items-center w-full text-md`}
                            onClick={() => markChartsAsPrinted([chart.id])}
                          >
                            Mark Chart as Printed
                          </span>
                        </div>
                      )}
                    </Menu.Item>
                  )}
                  {auth.isAdmin && (
                    <Menu.Item>
                      {({ active }) => (
                        <div className="flex justify-center text-center">
                          <ArrowPathIcon
                            className={`${active
                              ? "bg-primary-blue text-white border-1 border-primary-blue"
                              : "text-primary-blue"
                              } h-10 w-10 p-2 rounded-l-md`}
                            aria-hidden="true"
                          />
                          <span
                            //TODO: Create regenerateChart function that sends the current chartId to the endpoint
                            onClick={() => {
                              regenerateChart(chart.id);
                            }}
                            className={`${active
                              ? "bg-primary-blue text-white"
                              : "text-primary-blue"
                              } group flex rounded-r-md items-center w-full text-md`}
                          >
                            Regenerate Chart
                          </span>
                        </div>
                      )}
                    </Menu.Item>
                  )}
                  <Menu.Item>
                    {({ active }) => (
                      <div className="flex justify-center text-center">
                        <DocumentDuplicateIcon
                          className={`${active
                            ? "bg-primary-blue text-white border-1 border-primary-blue"
                            : "text-primary-blue"
                            } h-10 w-10 p-2 rounded-l-md`}
                          aria-hidden="true"
                        />
                        <span
                          onClick={() => {
                            navigate(
                              `/elections/${chart.electionType.name
                                .split(" ")[0]
                                .toLowerCase()}/duplicate/${chart.id}`
                            );
                          }}
                          className={`${active
                            ? "bg-primary-blue text-white"
                            : "text-primary-blue"
                            } group flex rounded-r-md items-center w-full text-md`}
                        >
                          Duplicate Chart
                        </span>
                      </div>
                    )}
                  </Menu.Item>
                  <Menu.Item>
                    {({ active }) => (
                      <div className="flex justify-center text-center">
                        <PencilSquareIcon
                          className={`${active
                            ? "bg-primary-blue text-white border-1 border-primary-blue"
                            : "text-primary-blue"
                            } h-10 w-10 p-2 rounded-l-md`}
                          aria-hidden="true"
                        />
                        <span
                          onClick={() => {
                            navigate(
                              `/elections/${chart.electionType.name
                                .split(" ")[0]
                                .toLowerCase() === 'non' ? 'non-partisan' : chart.electionType.name
                                  .split(" ")[0]
                                  .toLowerCase()}/edit/${chart.id}`,
                              { replace: false }
                            );
                          }}
                          className={`${active
                            ? "bg-primary-blue text-white"
                            : "text-primary-blue"
                            } group flex rounded-r-md items-center w-full text-md`}
                        >
                          Edit
                        </span>
                      </div>
                    )}
                  </Menu.Item>
                  <Menu.Item>
                    {({ active }) => (
                      <div className="flex justify-center text-center">
                        <TrashIcon
                          className={`${active
                            ? "bg-primary-blue text-white border-1 border-primary-blue"
                            : "text-primary-blue"
                            } h-10 w-10 p-2 rounded-l-md`}
                          aria-hidden="true"
                        />
                        <span
                          onClick={() => setDeleteConfirmationOpen(true)}
                          className={`${active
                            ? "bg-primary-blue text-white"
                            : "text-primary-blue"
                            } group flex rounded-r-md items-center w-full text-md`}
                        >
                          Delete
                        </span>
                      </div>
                    )}
                  </Menu.Item>
                </div>
              </Menu.Items>
            </Transition>
          </Menu>
        </div>
        <div></div>
      </div>
      <DeleteChartConfirmDialog
        isOpen={isDeleteConfirmationOpen}
        onDelete={() => deleteChart(chart.id)}
        onClose={() => setDeleteConfirmationOpen(false)}
      />
    </>
  );
};

export default ClerkChartDisplay;
